import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import PageHeader from '../components/ui/PageHeader';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import { deleteUserAccount, getDeleteAccountWarning } from '../services/accountService';
import { getBlobStats } from '../api/azureLibrary';
import toast from '../utils/toastUtils';

export default function Settings() {
  const { theme } = useTheme();
  const { userId, userName, userEmail } = useAuth();
  const { instance } = useMsal();
  const navigate = useNavigate();
  
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [fileCount, setFileCount] = useState(0);
  const [storageSize, setStorageSize] = useState('0 MB');

  useEffect(() => {
    loadAccountStats();
  }, [userId]);

  async function loadAccountStats() {
    if (!userId) return;
    
    try {
      const stats = await getBlobStats(userId);
      setFileCount(stats.totalFiles);
      setStorageSize(formatBytes(stats.totalSize));
    } catch (error) {
      console.error('Failed to load account stats:', error);
    }
  }

  function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  async function handleDeleteAccount() {
    if (confirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    if (!userId) {
      toast.error('User not authenticated');
      return;
    }

    setIsDeleting(true);

    try {
      await deleteUserAccount(userId);

      toast.success('Account deleted successfully');

      setTimeout(() => {
        instance.logoutRedirect({
          postLogoutRedirectUri: '/',
        });
      }, 2000);

    } catch (error) {
      console.error('Delete account error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete account');
      setIsDeleting(false);
    }
  }

  function handleChangePassword() {
    toast.info(
      `To reset your password:\n\n` +
      `1. Sign out from the app\n` +
      `2. Click "Sign In"\n` +
      `3. Click "Forgot your password?"\n` +
      `4. Follow the reset instructions`,
      10000
    );
  }

  return (
    <div>
      <PageHeader
        title="Account Settings"
        subtitle="Manage your account, preferences, and data."
      />

      {/* Profile Information */}
      <Card>
        <h2 style={{ color: theme.colors.textPrimary, marginBottom: '16px', fontSize: '20px' }}>
          Profile Information
        </h2>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <label style={{ color: theme.colors.textSecondary, fontSize: '14px', display: 'block', marginBottom: '4px' }}>
              Name
            </label>
            <p style={{ color: theme.colors.textPrimary, fontSize: '16px', margin: 0 }}>
              {userName || 'Not available'}
            </p>
          </div>

          <div>
            <label style={{ color: theme.colors.textSecondary, fontSize: '14px', display: 'block', marginBottom: '4px' }}>
              Email
            </label>
            <p style={{ color: theme.colors.textPrimary, fontSize: '16px', margin: 0 }}>
              {userEmail || 'Not available'}
            </p>
          </div>

          <div>
            <label style={{ color: theme.colors.textSecondary, fontSize: '14px', display: 'block', marginBottom: '4px' }}>
              User ID
            </label>
            <p style={{ 
              color: theme.colors.textSecondary, 
              fontSize: '12px', 
              fontFamily: 'monospace',
              margin: 0,
              wordBreak: 'break-all'
            }}>
              {userId}
            </p>
          </div>
        </div>
      </Card>

      {/* Storage Usage */}
      <div style={{ marginTop: '20px' }}>
        <Card>
          <h2 style={{ color: theme.colors.textPrimary, marginBottom: '16px', fontSize: '20px' }}>
            Storage Usage
          </h2>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: theme.colors.textSecondary }}>Total Files:</span>
              <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>{fileCount}</span>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: theme.colors.textSecondary }}>Storage Used:</span>
              <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>{storageSize}</span>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: theme.colors.textSecondary }}>Upload Limit:</span>
              <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>100 files/hour</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Account Management */}
      <div style={{ marginTop: '20px' }}>
        <Card>
          <h2 style={{ color: theme.colors.textPrimary, marginBottom: '16px', fontSize: '20px' }}>
            Account Management
          </h2>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Button 
              variant="secondary" 
              onClick={handleChangePassword}
              style={{ width: '100%' }}
            >
              🔑 How to Reset Password
            </Button>
            
            <p style={{ 
              color: theme.colors.textSecondary, 
              fontSize: '13px', 
              margin: '8px 0',
              fontStyle: 'italic'
            }}>
              To change your email address, please contact support or create a new account.
            </p>
          </div>
        </Card>
      </div>

      {/* Danger Zone */}
      <div style={{ marginTop: '20px' }}>
        <Card>
          <div style={{
            border: `2px solid ${theme.colors.error}`,
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: 'rgba(239, 68, 68, 0.05)'
          }}>
            <h2 style={{ color: theme.colors.error, marginBottom: '16px', fontSize: '20px' }}>
              ⚠️ Danger Zone
            </h2>
            
            <p style={{ color: theme.colors.textSecondary, marginBottom: '8px', fontSize: '14px' }}>
              Deleting your account is permanent and cannot be undone. The following will happen immediately:
            </p>
            <ul style={{ color: theme.colors.textSecondary, fontSize: '14px', marginBottom: '16px', paddingLeft: '20px', lineHeight: '1.8' }}>
              <li>All your files will be permanently deleted</li>
              <li>Your subscription will be cancelled</li>
              <li>Your account will be removed</li>
              <li>You will not be charged further</li>
            </ul>
            <p style={{ color: theme.colors.textSecondary, marginBottom: '16px', fontSize: '14px' }}>
              Please download any files you wish to keep <strong style={{ color: theme.colors.error }}>before</strong> deleting your account.
            </p>
            
            <Button 
              variant="secondary"
              onClick={() => setShowDeleteModal(true)}
              style={{ 
                backgroundColor: theme.colors.error,
                color: '#fff',
                border: 'none'
              }}
            >
              Delete Account
            </Button>
          </div>
        </Card>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
          }}
          onClick={() => !isDeleting && setShowDeleteModal(false)}
        >
          <div
            style={{
              background: theme.colors.bgSecondary,
              padding: '32px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              border: `2px solid ${theme.colors.error}`
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ color: theme.colors.error, marginBottom: '20px', fontSize: '24px' }}>
              ⚠️ Delete Account
            </h2>
            
            <div style={{ 
              color: theme.colors.textPrimary, 
              marginBottom: '24px',
              whiteSpace: 'pre-line',
              fontSize: '14px',
              lineHeight: '1.6'
            }}>
              {getDeleteAccountWarning(fileCount, storageSize)}
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ 
                color: theme.colors.textPrimary, 
                fontSize: '14px', 
                display: 'block', 
                marginBottom: '8px',
                fontWeight: 500
              }}>
                Type <strong style={{ color: theme.colors.error }}>DELETE</strong> to confirm:
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                disabled={isDeleting}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: theme.colors.bgPrimary,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '6px',
                  color: theme.colors.textPrimary,
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button
                variant="secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              
              <Button
                variant="primary"
                onClick={handleDeleteAccount}
                disabled={isDeleting || confirmText !== 'DELETE'}
                style={{
                  backgroundColor: theme.colors.error,
                  opacity: confirmText !== 'DELETE' ? 0.5 : 1,
                  cursor: confirmText !== 'DELETE' ? 'not-allowed' : 'pointer'
                }}
              >
                {isDeleting ? '⏳ Deleting...' : 'Delete My Account'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
